import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subject, takeUntil } from 'rxjs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { CstIcmsTexto, getCstIcmsTexto } from '@shared/constants/motor-fiscal/cst-icms-metadata';
import {
    IndicadoresCst,
    MapaCamposIndicador,
    aplicarObrigatoriedadeIndicadores,
    controlesDasRegrasEntreCampos,
    isAlgumCampoAtivo,
    isCampoAtivo,
    isCodigoDaOperacao,
    lerIndicadoresCst,
    limparCamposInativos,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { MaskService } from '@shared/services/mask.service';
import { CstIcmsStruct } from '@shared/services/structs/motor-fiscal/cst-icms.struct';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';

/**
 * Campo do indicador do CST do ICMS -> control do grupo `icms` do item da venda. `vICMSDeson` só alimenta a regra
 * do motivo da desoneração; `valorBaseFcpSt` é limpo junto com o FCP-ST.
 */
const MAPA_CAMPOS_ICMS_VENDA: MapaCamposIndicador = {
    modBC: 'codigoModalidadeBaseCalculo',
    pRedBC: 'percentualReducaoBaseCalculo',
    pICMS: 'percentualAliquota',
    pFCP: 'percentualFcp',
    modBCST: 'codigoModalidadeBaseCalculoSt',
    pMVAST: 'percentualMvaSt',
    pRedBCST: 'percentualReducaoBaseCalculoSt',
    pICMSST: 'percentualAliquotaSt',
    pFCPST: ['percentualFcpSt', 'valorBaseFcpSt'],
    pDif: 'percentualDiferimento',
    pRedAdRem: 'percentualReducaoAdrem',
    motDesICMS: 'codigoMotivoDesoneracao',
    vICMSDeson: 'valorIcmsDesonerado',
    pST: 'percentualSuporteStRetido',
    pFCPSTRet: 'percentualFcpStRetido',
    pICMSEfet: 'percentualAliquotaEfetiva',
    qBCMono: 'quantidadeBaseMonofasica',
    adRemICMS: 'valorAliquotaAdremMonofasica',
    qBCMonoReten: 'quantidadeBaseMonofasicaRetencao',
    adRemICMSReten: 'valorAliquotaAdremMonofasicaRetencao',
    qBCMonoRet: 'quantidadeBaseMonofasicaRetido',
    adRemICMSRet: 'valorAliquotaAdremMonofasicaRetido',
};

/**
 * Aba "ICMS" (regime normal) do modal do item da venda (cópia da aba ICMS do item da NF-e de
 * saída). Os indicadores do CST (`indicadores-campos.util`) decidem o que aparece, o que é
 * obrigatório e o que é limpo quando o usuário troca o CST. Grava no grupo `icms` do item.
 */
@Component({
    selector: 'app-vd-item-aba-icms',
    standalone: true,
    imports: [
        AbntPipe,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        NgxCurrencyDirective,
        AlertCardComponent,
        EmptyStateComponent,
    ],
    templateUrl: './vd-item-aba-icms.component.html',
    styleUrls: ['../abas-comum.css', './vd-item-aba-icms.component.css'],
})
export class VdItemAbaIcmsComponent implements OnInit, OnChanges, OnDestroy {
    /** O grupo `icms` do item. */
    @Input({ required: true }) public form!: FormGroup;
    @Input() public listaCstIcms: CstIcmsStruct[] = [];
    @Input() public listaModalidadeBc: ModalidadeBcOption[] = [];
    @Input() public listaModalidadeBcSt: ModalidadeBcOption[] = [];
    @Input() public listaMotivoDesoneracao: LookupItemStruct[] = [];
    @Input() public isRecalculoPendente: boolean = false;

    public readonly percentMask = this.maskService.getPercentMask();
    public readonly valorMask = this.maskService.getCurrencyMask(2);
    public readonly quantidadeMask = this.maskService.getDecimalMask(4);

    private readonly destroy$ = new Subject<void>();

    constructor(private maskService: MaskService) {}

    public ngOnInit(): void {
        this.form.get('codigoCst')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.sincronizarObrigatoriedade());
        // Regra entre campos: o motivo da desoneração só é exigido com ICMS desonerado apurado.
        controlesDasRegrasEntreCampos(MAPA_CAMPOS_ICMS_VENDA).forEach((campo) => this.form.get(campo)?.valueChanges
            .pipe(takeUntil(this.destroy$)).subscribe(() => this.sincronizarObrigatoriedade()));
        this.sincronizarObrigatoriedade();
    }

    /** Os indicadores chegam com a lista de CST (lookup assíncrono). */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['listaCstIcms'] && !changes['listaCstIcms'].firstChange) this.sincronizarObrigatoriedade();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get indicadores(): IndicadoresCst {
        const cst = this.form?.get('codigoCst')?.value;

        return lerIndicadoresCst(this.listaCstIcms.find((c) => c.codigo === cst)?.indicadores);
    }

    public get cstIcmsTexto(): CstIcmsTexto | null {
        return getCstIcmsTexto(this.form?.get('codigoCst')?.value);
    }

    public get isComCst(): boolean {
        return !!this.form?.get('codigoCst')?.value;
    }

    /** Só os CSTs de saída, pelo indicador. */
    public get listaCstIcmsDaOperacao(): CstIcmsStruct[] {
        return this.listaCstIcms.filter((c) => isCodigoDaOperacao(c.indicadores, TipoOperacaoEnum.Saida));
    }

    public ativo(chave: string): boolean {
        return isCampoAtivo(this.indicadores, chave);
    }

    public algumAtivo(chaves: string[]): boolean {
        return isAlgumCampoAtivo(this.indicadores, chaves);
    }

    public get isMonofasico(): boolean {
        return this.algumAtivo(['qBCMono', 'adRemICMS', 'qBCMonoReten', 'adRemICMSReten', 'qBCMonoRet', 'adRemICMSRet']);
    }

    /** Há valor apurado no grupo que o CST usa (ICMS próprio, ST, ST retido ou monofásico). */
    public get isComValoresApurados(): boolean {
        if (this.algumAtivo(['pST', 'pFCPSTRet'])) {
            return this.form.get('valorIcmsStRetido')?.value != null || this.form.get('valorIcms')?.value != null;
        }

        if (this.isMonofasico) {
            return ['valorIcmsMonofasico', 'valorIcmsMonofasicoRetencao', 'valorIcmsMonofasicoRetido']
                .some((campo) => this.form.get(campo)?.value != null);
        }

        return this.form.get('valorIcms')?.value != null || this.form.get('valorIcmsSt')?.value != null;
    }

    /** Troca feita pelo usuário: zera os campos que o novo CST deixou de exibir (evita valor obsoleto no cálculo). */
    public onCstSelecionado(): void {
        limparCamposInativos(this.form, this.indicadores, MAPA_CAMPOS_ICMS_VENDA);
        this.sincronizarObrigatoriedade();
    }

    /** Mesma regra da natureza: obrigatório pelo indicador do CST e pelas regras entre campos. */
    private sincronizarObrigatoriedade(): void {
        const isComCst = !!this.form.get('codigoCst')?.value;

        aplicarObrigatoriedadeIndicadores(this.form, isComCst ? this.indicadores : null, MAPA_CAMPOS_ICMS_VENDA);
    }
}
